import React, { useState } from 'react';
import { Menu, PanelLeftClose, PanelLeft } from 'lucide-react';
import { MainNav } from '../MainNav';
import { useLocalStorage } from '../../../hooks';
import styles from './AppLayout.module.css';

export interface AppLayoutProps {
  children: React.ReactNode;
  sidebar?: React.ReactNode;
  rightPanel?: React.ReactNode;
  hideLayout?: boolean;
}

export const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  sidebar,
  rightPanel,
  hideLayout = false,
}) => {
  const { value: sidebarCollapsed, setValue: setSidebarCollapsed } = useLocalStorage('sidebar-collapsed', false);
  const { value: navCollapsed } = useLocalStorage('mainnav-collapsed', false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const user = {
    name: 'Umapathi',
    avatar: undefined,
  };

  // If hideLayout is true, render children without the layout
  if (hideLayout) {
    return <>{children}</>;
  }

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const toggleMobileSidebar = () => {
    setMobileSidebarOpen(!mobileSidebarOpen);
  };

  // Compute CSS custom property for nav width
  const navWidth = navCollapsed ? 64 : 220;

  return (
    <div className={styles.appLayout} style={{ '--nav-width': `${navWidth}px` } as React.CSSProperties}>
      <MainNav user={user} />
      
      {sidebar && (
        <>
          {/* Sidebar */}
          <aside className={`${styles.sidebar} ${sidebarCollapsed ? styles.collapsed : ''} ${mobileSidebarOpen ? styles.open : ''}`}>
            {sidebar}
          </aside>

          {/* Desktop collapse toggle — edge-hugging pill */}
          <div
            className={`${styles.toggleZone} ${sidebarCollapsed ? styles.collapsed : ''}`}
          >
            <button
              type="button"
              className={`${styles.sidebarToggle} ${sidebarCollapsed ? styles.collapsed : ''}`}
              onClick={toggleSidebar}
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <span className={styles.toggleLine} />
              <span className={styles.toggleIcon}>
                {sidebarCollapsed ? <PanelLeft size={14} /> : <PanelLeftClose size={14} />}
              </span>
              <span className={styles.toggleLine} />
            </button>
          </div>

          {/* Mobile backdrop */}
          {mobileSidebarOpen && (
            <div className={styles.backdrop} onClick={toggleMobileSidebar} />
          )}
        </>
      )}
      
      <main className={styles.mainContent}>
        {/* Mobile sidebar toggle */}
        {sidebar && (
          <button
            type="button"
            className={styles.mobileSidebarToggle}
            onClick={toggleMobileSidebar}
            aria-label="Toggle sidebar"
          >
            <Menu size={20} />
          </button>
        )}
        {children}
      </main>
      
      {rightPanel && (
        <aside className={styles.rightPanel}>
          {rightPanel}
        </aside>
      )}
    </div>
  );
};

export default AppLayout;
